import React, {useEffect, useMemo, useState} from "react";
import {useObserver} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {Button, Col, Row, Skeleton, Spin, Tooltip} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {SelectField} from "./EditPoolUsers.styles";
import {arrayAlphabeticalSort, formatPhoneNumber} from "../../../utils/helpers";
import debounce from "lodash/debounce";
import {useHistory} from "react-router-dom";

const EditPoolUsers = ({poolId}) => {
    const history = useHistory();
    const {t} = useTranslation([]);
    const stores = useStore();

    const [searchText, setSearchText] = useState();

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [loadingUsers] = useFetch({
        fetchFn: stores.userStore.getUserList.bind(null, {
            limit: 20,
            skip: 0,
            searchText,
            role: "all",
            activity: true,
            excludedRoles: "cUser"
        }),
        defaultDataValue: {rows: []},
        dependencies: [searchText],
        initialCall: false,
        callbackFunc: data => {
            const oldUserOptions = [...userOptions];
            data.rows
                .filter(
                    user => oldUserOptions.findIndex(oldUser => oldUser._id === user._id) === -1
                )
                .filter(user => !["cUser"].includes(user?.role))
                .forEach(user => {
                    oldUserOptions.push(user);
                });
            setUserOptions([...oldUserOptions]);
        }
    });
    const debounceTimeout = 400;
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [loading, poolAuthorizedUsers] = useFetch({
        fetchFn: stores.poolStore.getPoolAuthorizedUsers.bind(null, {poolId}),
        initialCall: true,
        defaultDataValue: undefined
    });

    const initSelectedUsers = ({userIds}) => {
        setSelectedUsers(prev => [
            ...prev,
            ...userIds.filter(userId => prev.findIndex(prevUserId => prevUserId === userId) === -1)
        ]);
    };
    const renderUserOptions = () => {
        {
            const users = [...userOptions];
            if (poolAuthorizedUsers && poolAuthorizedUsers?.data) {
                poolAuthorizedUsers.data
                    .filter(u => users.findIndex(user => user._id === u._id) === -1)
                    .forEach(user => {
                        users.push(user);
                    });
            }

            return arrayAlphabeticalSort([...users], "fullName").map(user => {
                return (
                    <SelectField.Option
                        key={user.fullName}
                        value={user._id}
                        data-testid="poolAuthorizedUserSelect_option"
                    >
                        <Tooltip title={user?.error?.reason || ""}>
                            <Row>
                                <Col>
                                    <Row style={{alignItems: "center"}}>
                                        {user.fullName}
                                        {user?.hasError ? (
                                            <InfoCircleOutlined style={{marginLeft: "5px"}} />
                                        ) : (
                                            <></>
                                        )}
                                    </Row>
                                    <Row>{formatPhoneNumber(user.phone)}</Row>
                                </Col>
                            </Row>
                        </Tooltip>
                    </SelectField.Option>
                );
            });
        }
    };

    const handleUpdatePoolAuthorizedUserSubmit = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                poolId,
                userIds: selectedUsers
            };
            await stores.poolStore.setPoolAuthorizedUsers(payload);
        } catch (err) {
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSearchUser = useMemo(
        value => {
            const loadOptions = value => {
                setSearchText(value);
            };
            return debounce(loadOptions, debounceTimeout);
        },
        [debounceTimeout]
    );

    useEffect(() => {
        if (!poolAuthorizedUsers || !poolAuthorizedUsers?.data || loading) return;
        initSelectedUsers({userIds: poolAuthorizedUsers?.data?.map(user => user._id) || []});
    }, [JSON.stringify(poolAuthorizedUsers)]);

    return useObserver(() => (
        <>
            <Row style={{justifyContent: "space-between", marginBottom: "16px"}}>
                <b onClick={() => history.goBack()} style={{cursor: "pointer"}}>
                    {"<"} Geri Dön
                </b>
            </Row>
            <Row type="flex" justify="center" data-testid="editPoolScreen" gutter={[0, 20]}>
                {loading && poolAuthorizedUsers?.data ? (
                    <Skeleton paragraph={{rows: 2, width: "100%"}}></Skeleton>
                ) : (
                    <>
                        <Col span={20}>
                            <h1>{"Havuz Yetkililerini Düzenle"}</h1>
                            <Col span={24} data-testid="poolCouriers">
                                <h4>{"Havuz Yetkilileri"}</h4>
                                <SelectField
                                    notFoundContent={loadingUsers ? <Spin size="small" /> : null}
                                    allowClear
                                    style={{width: "100%"}}
                                    value={selectedUsers}
                                    data-testid="poolAuthorizedUserSelect"
                                    onChange={value => {
                                        setSelectedUsers(value);
                                    }}
                                    onSearch={onSearchUser}
                                    loading={loadingUsers}
                                    showSearch
                                    mode="multiple"
                                    filterOption={(input, option) =>
                                        option.key.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {renderUserOptions()}
                                </SelectField>
                            </Col>
                            <Col
                                span="24"
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    marginTop: "10px"
                                }}
                            >
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    disabled={isSubmitting}
                                    onClick={handleUpdatePoolAuthorizedUserSubmit}
                                >
                                    {t("actions.update", {ns: "common"})}
                                </Button>
                            </Col>
                        </Col>
                    </>
                )}
            </Row>
        </>
    ));
};

export default EditPoolUsers;
